import React, { useEffect, useState, useCallback } from "react";
import { IntlShape } from "react-intl";
import { FieldAttributes } from "formik";
import { match as Match } from "react-router-dom";
import { toast } from "react-toastify";
import Block from "../../../../../components/Tailwind/Block";
import Header from "../../../../../components/Tailwind/Block/Header";
import DefaultForm, {
  AdditionalFieldAttributes,
} from "../../../../../components/Form";
import overrideClasses from "../../../../../utils/overrideClasses";
import { GroundGraphqlContextStore } from "../../../../../lib/ground-aws-graphql-core";
import {
  getCategoryItemsForm,
  parseTranslatable,
} from "../../../../../utils/types";
import history from "../../../../../history";
import { EnumPaths } from "../../../../../utils/navigation";
import FormSwitch from "../../../../../components/Form/FormSwitch";
import {
  EnumCategoryType,
  SearchableCategorySortableFields,
  SearchableSortDirection,
} from "../../../../../lib/ground-aws-graphql-core/api/graphql/types";
import contextStore from "../../../../../redux/store";
import { getLocale } from "../../../../../lang";
import FormQuill from "../../../../../components/Form/FormQuill";

interface Props {
  match?: Match<{ id: string }>;
  intl: IntlShape;
  edition: boolean;
}

interface FormValues {
  code: string;
  label: string | Record<string, string>;
  required: boolean;
  description?: string | Record<string, string>;
  is_special_event?: boolean;
  is_bookable?: boolean;
  minimum_approval_time?: number;
  should_select_product?: boolean;
  category_id?: string;
}

const QuoteRequestServiceForm: React.FC<Props> = ({ intl, match, edition }) => {
  const [loading, setLoading] = useState(false);
  const [togglePanel, setTogglePanel] = useState(false);

  const {
    getQuoteRequestService,
    createQuoteRequestService,
    updateQuoteRequestService,
    setQuoteRequestService,
  } = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests
  );

  const searchAllCategories = GroundGraphqlContextStore.useStoreActions(
    actions => actions.category.searchAllCategories
  );

  const quoteRequestService = GroundGraphqlContextStore.useStoreState(
    state => state.quoteRequests.quoteRequestService
  );

  const categories = GroundGraphqlContextStore.useStoreState(
    state => state.category.categories.items
  );

  const locale = contextStore.useStoreState(state => state.settings.locale);
  const currentAppLocale = getLocale(locale);

  const categoryItemsForm = getCategoryItemsForm(intl, categories);

  /** 🔄 Fetch categories + service data */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await searchAllCategories({
        filter: { type: { eq: EnumCategoryType.SERVICE } },
        sort: {
          field: SearchableCategorySortableFields.position,
          direction: SearchableSortDirection.asc,
        },
        locale: currentAppLocale.backend_locale,
      });

      if (match?.params.id) {
        await getQuoteRequestService({ id: match.params.id });
      }
    } finally {
      setLoading(false);
    }
  }, [searchAllCategories, getQuoteRequestService, match, currentAppLocale]);

  useEffect(() => {
    fetchData();

    return () => setQuoteRequestService(null);
  }, [fetchData, setQuoteRequestService]);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      setLoading(true);
      try {
        const { label, description, ...others } = values;

        const payload = {
          ...others,
          label: parseTranslatable(label),
          description: parseTranslatable(description),
        };

        if (edition && quoteRequestService) {
          await updateQuoteRequestService({
            ...payload,
            id: quoteRequestService.id,
          });
          toast(
            intl.formatMessage({
              id: "page.quote.request.service.update.success",
            }),
            {
              type: "success",
            }
          );
        } else {
          await createQuoteRequestService(payload);
          toast(
            intl.formatMessage({
              id: "page.quote.request.service.create.success",
            }),
            {
              type: "success",
            }
          );
        }

        history.push(`/${EnumPaths.ROOT}/${EnumPaths.QUOTE_REQUEST_SERVICES}`);
      } catch (error) {
        toast(
          intl.formatMessage({
            id: edition
              ? "page.quote.request.service.update.error"
              : "page.quote.request.service.create.error",
          }),
          { type: "error" }
        );
      } finally {
        setLoading(false);
      }
    },
    [
      edition,
      intl,
      quoteRequestService,
      updateQuoteRequestService,
      createQuoteRequestService,
    ]
  );

  /** 📋 Form fields */
  const formFields: FieldAttributes<AdditionalFieldAttributes>[] = [
    {
      name: "code",
      label: "page.quote.request.service.code",
      placeholder: "page.quote.request.service.code.placeholder",
      initialValue: quoteRequestService?.code,
      required: true,
    },
    {
      name: "label",
      label: "page.quote.request.service.label",
      placeholder: "page.quote.request.service.label.placeholder",
      initialValue: quoteRequestService?.label,
      required: true,
      translatable: true,
    },
    {
      name: "description",
      label: "page.quote.request.service.description",
      placeholder: "page.quote.request.service.description.placeholder",
      initialValue: quoteRequestService?.description,
      translatable: true,
      component: formikProps => <FormQuill {...formikProps} />,
    },
    {
      name: "is_special_event",
      label: "page.quote.request.service.is_special_event",
      initialValue: quoteRequestService?.is_special_event ?? false,
      component: props => (
        <FormSwitch
          {...props}
          description="page.quote.request.service.is_special_event.description"
        />
      ),
    },
    {
      name: "required",
      label: "page.quote.request.service.required",
      initialValue: quoteRequestService?.required ?? true,
      component: props => (
        <FormSwitch
          {...props}
          description="page.quote.request.service.required.description"
        />
      ),
    },
    {
      name: "is_bookable",
      label: "page.quote.request.service.is_bookable",
      initialValue: quoteRequestService?.is_bookable ?? false,
      component: props => (
        <FormSwitch
          {...props}
          description="page.quote.request.service.is_bookable.description"
        />
      ),
    },
    {
      name: "minimum_approval_time",
      type: "number",
      label: "page.quote.request.service.minimum_approval_time",
      placeholder:
        "page.quote.request.service.minimum_approval_time.placeholder",
      initialValue: quoteRequestService?.minimum_approval_time,
      min: 1,
    },
    {
      name: "should_select_product",
      label: "page.quote.request.service.should_select_product",
      initialValue: quoteRequestService?.should_select_product ?? false,
      component: props => (
        <FormSwitch
          {...props}
          description="page.quote.request.service.should_select_product.description"
        />
      ),
    },
    {
      name: "category_id",
      label: "general.category",
      placeholder: "general.category",
      initialValue: quoteRequestService?.category_id,
      as: "select",
      options: categoryItemsForm,
    },
  ];

  if (loading) return <div className="loading" />;

  return (
    <div className={overrideClasses("px-8 pb-32", { hidden: loading })}>
      <Block>
        <Header
          item={quoteRequestService}
          title={
            edition
              ? "page.list.quote.request.services.update.quote.request.service"
              : "page.list.quote.request.services.create.quote.request.service"
          }
          className="border-b border-gray-200"
        />
        <DefaultForm
          fields={formFields}
          onSubmit={handleSubmit}
          showPanel={togglePanel}
        />
      </Block>
    </div>
  );
};

export default QuoteRequestServiceForm;
